import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';
import { StandardEvents } from '@shopify/events';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  static validLayouts = {
    desktop: ['desktop-3', 'default', 'desktop-6'],
    mobile: ['mobile-single', 'default'],
  };

  #publishedPriceSortRun = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#renderPublishedPricePage();
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleCollectionUpdate);
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#publishedPriceSortRun += 1;
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleCollectionUpdate);
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Re-runs published-price sorting after collection filtering or sorting replaces the section markup.
   *
   * @param {Event & { promise?: Promise<unknown> }} event
   */
  #handleCollectionUpdate = async (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;

    try {
      await event.promise;
    } catch {
      return;
    }

    if (this.isConnected) this.#renderPublishedPricePage();
  };

  /**
   * Shopify can sort with prices from variants that are not published to the storefront.
   * Gather every server-paginated result, sort by the card's published price, and render
   * the correct slice for the current page.
   */
  async #renderPublishedPricePage() {
    const direction = this.getAttribute('price-sort');
    if (direction !== 'price-ascending' && direction !== 'price-descending') return;

    const { grid } = this.refs;
    if (!(grid instanceof HTMLUListElement)) return;

    const currentItems = this.#getPublishedPriceItems(grid);
    if (currentItems.length === 0) return;

    const currentPage = Number(currentItems[0].dataset.page) || 1;
    const lastPage = Number(grid.dataset.lastPage) || 1;
    const pageSize = Number(this.getAttribute('price-sort-page-size')) || currentItems.length;
    const run = ++this.#publishedPriceSortRun;

    grid.setAttribute('aria-busy', 'true');
    this.setAttribute('published-price-sort-pending', '');

    try {
      const allItems = [...currentItems];
      const pageNumbers = Array.from({ length: lastPage }, (_, index) => index + 1).filter(
        (page) => page !== currentPage
      );

      // Limit concurrent section requests so larger collections do not overwhelm the browser or storefront.
      for (let index = 0; index < pageNumbers.length; index += 4) {
        const batch = pageNumbers.slice(index, index + 4);
        const pages = await Promise.all(batch.map((page) => this.#getPublishedPricePage(page)));

        if (!this.isConnected || run !== this.#publishedPriceSortRun) return;
        for (const items of pages) allItems.push(...items);
      }

      const multiplier = direction === 'price-ascending' ? 1 : -1;
      const sortedItems = allItems
        .map((item, index) => ({ item, index }))
        .sort((first, second) => {
          const firstPrice = Number(first.item.dataset.visiblePrice);
          const secondPrice = Number(second.item.dataset.visiblePrice);
          return (firstPrice - secondPrice) * multiplier || first.index - second.index;
        })
        .map(({ item }) => item);

      const pageStart = (currentPage - 1) * pageSize;
      const pageItems = sortedItems.slice(pageStart, pageStart + pageSize);

      if (!this.isConnected || run !== this.#publishedPriceSortRun || this.refs.grid !== grid) return;

      for (const item of currentItems) item.remove();
      pageItems.forEach((item, index) => {
        item.dataset.page = currentPage.toString();
        this.#normalizeSortedCard(item, index);
      });
      grid.append(...pageItems);
    } catch (error) {
      console.warn('[results-list] Unable to sort by published storefront prices.', error);
    } finally {
      if (run === this.#publishedPriceSortRun) {
        grid.removeAttribute('aria-busy');
        this.removeAttribute('published-price-sort-pending');
        this.setAttribute('published-price-sort-ready', '');
      }
    }
  }

  /**
   * @param {number} page
   * @returns {Promise<HTMLElement[]>}
   */
  async #getPublishedPricePage(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    const sectionHTML = await sectionRenderer.getSectionHTML(this.sectionId, true, url);
    const parsedPage = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const grid = parsedPage.querySelector('[ref="grid"]');

    return grid instanceof HTMLUListElement ? this.#getPublishedPriceItems(grid) : [];
  }

  /**
   * @param {HTMLUListElement} grid
   * @returns {HTMLElement[]}
   */
  #getPublishedPriceItems(grid) {
    return Array.from(grid.querySelectorAll(':scope > .product-grid__item[data-visible-price]')).filter(
      (item) => item instanceof HTMLElement
    );
  }

  /**
   * Prevent duplicate card IDs when cards from different Shopify pages are combined.
   *
   * @param {HTMLElement} item
   * @param {number} index
   */
  #normalizeSortedCard(item, index) {
    for (const className of [...item.classList]) {
      if (className.startsWith('product-grid__item--')) item.classList.remove(className);
    }
    item.classList.add(`product-grid__item--${index}`);

    const productCard = item.querySelector('product-card');
    if (productCard && item.dataset.productId) {
      productCard.id = `product-card-${this.sectionId}-${item.dataset.productId}`;
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    if (!ResultsList.validLayouts[viewport].includes(value)) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const viewport = event.matches ? 'desktop' : 'mobile';
    const storedLayout = sessionStorage.getItem(`product-grid-view-${viewport}`);
    const value = storedLayout && ResultsList.validLayouts[viewport].includes(storedLayout) ? storedLayout : 'default';
    const targetElement = this.querySelector(`input[value="${value}"][name="grid${event.matches ? '' : '-mobile'}"]`);

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout(value);
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
